import React, { useEffect, useRef } from 'react';

interface GoogleAddressInputProps {
  label: string;
  name: string;
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  error?: string;
  required?: boolean;
  className?: string;
}

const GoogleAddressInput: React.FC<GoogleAddressInputProps> = ({
  label,
  onChange,
  error,
  required,
}) => {
  const inputRef = useRef<HTMLDivElement>(null);
  const autocompleteRef = useRef<google.maps.places.PlaceAutocompleteElement | null>(null);

  useEffect(() => {
    const initAutocomplete = async () => {
      await google.maps.importLibrary("places");
      
      if (inputRef.current && !autocompleteRef.current) {
        const autocomplete = new google.maps.places.PlaceAutocompleteElement();
        autocompleteRef.current = autocomplete;
        
        autocomplete.addEventListener('gmp-select', async ({ placePrediction }) => {
          const place = placePrediction.toPlace();
          await place.fetchFields({ fields: ['displayName'] });
          const placeData = place.toJSON();
          onChange(placeData.displayName || '');
        });

        inputRef.current.appendChild(autocomplete);
      }
    };

    initAutocomplete();

    return () => {
      if (autocompleteRef.current) {
        autocompleteRef.current.remove();
      }
    };
  }, []);

  return (
    <div className="mb-4">
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <div ref={inputRef} />
      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}
    </div>
  );
};

export default GoogleAddressInput; 